import { useState, useEffect, useRef } from "react";
import { BRAND, btn, text, SHADOW_MODAL, WHITE, MUTED, SCRIM, SCRIM_PHOTO } from "./constants";

const ImageCropModal = ({ imageSrc, onConfirm, onCancel, uploading, lang = "en" }) => {
  const canvasRef = useRef(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const imgRef = useRef(null);
  const SIZE = 280;   // UI preview size
  const EXPORT_SIZE = 600; // Saved resolution

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const scale = Math.max(SIZE / img.width, SIZE / img.height);
      setZoom(scale);
      setOffset({ x: (SIZE - img.width * scale) / 2, y: (SIZE - img.height * scale) / 2 });
    };
    img.src = imageSrc;
  }, [imageSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.save();
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(imgRef.current, offset.x, offset.y, imgRef.current.width * zoom, imgRef.current.height * zoom);
    ctx.restore();
    // Overlay outside circle
    ctx.fillStyle = SCRIM;
    ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    // Redraw image inside circle
    ctx.save();
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(imgRef.current, offset.x, offset.y, imgRef.current.width * zoom, imgRef.current.height * zoom);
    ctx.restore();
    // Circle border
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.strokeStyle = BRAND;
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [offset, zoom]);

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(z => Math.max(0.1, Math.min(10, z * delta)));
  };

  const handleMouseDown = (e) => { setDragging(true); setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y }); };
  const handleMouseMove = (e) => { if (!dragging) return; setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); };
  const handleMouseUp = () => setDragging(false);

  const handleConfirm = () => {
    const scale = EXPORT_SIZE / SIZE;
    const out = document.createElement("canvas");
    out.width = EXPORT_SIZE; out.height = EXPORT_SIZE;
    const ctx = out.getContext("2d");
    ctx.save();
    ctx.beginPath();
    ctx.arc(EXPORT_SIZE / 2, EXPORT_SIZE / 2, EXPORT_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(imgRef.current, offset.x * scale, offset.y * scale, imgRef.current.width * zoom * scale, imgRef.current.height * zoom * scale);
    ctx.restore();
    out.toBlob(blob => onConfirm(blob), "image/png", 0.95);
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: SCRIM_PHOTO, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: WHITE, borderRadius: 16, padding: 28, boxShadow: SHADOW_MODAL, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <div style={text.cardTitle}>{lang === "es" ? "Recortar foto" : "Crop photo"}</div>
        <div style={{ fontSize: 12, color: MUTED }}>{lang === "es" ? "Arrastra para recolocar · Usa la rueda para hacer zoom" : "Drag to reposition · Scroll to zoom"}</div>
        <canvas ref={canvasRef} width={SIZE} height={SIZE}
          style={{ borderRadius: "50%", cursor: dragging ? "grabbing" : "grab", userSelect: "none" }}
          onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
          onWheel={handleWheel} />
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onCancel} style={btn.ghost}>{lang === "es" ? "Cancelar" : "Cancel"}</button>
          <button onClick={handleConfirm} disabled={uploading} style={uploading ? btn.disabled : btn.primary}>
            {uploading ? (lang === "es" ? "Subiendo..." : "Uploading...") : (lang === "es" ? "Guardar foto" : "Save photo")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
